import graphql__3455b2c26090aa08ea9d from "./__generated__/MarketingEmailViewQuery.graphql.ts";
import graphql__bb52042670b7bd167f39 from "./__generated__/MarketingEmailViewRenameMutation.graphql.ts";import * as React from "react";
import {useState, useCallback} from "react";
import {graphql, useMutation} from "react-relay";
import {Outlet} from "react-router";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import {loadQuery, usePreloadedQuery} from "react-relay/hooks";
import {useIntl} from "react-intl";
import BreadcrumbNavigation from "./navigation/BreadcrumbNavigation";
import SecondaryNavigation from "./navigation/SecondaryNavigation";
import MarketingHeader from "./presentational/MarketingHeader";
import ActionButton from "./presentational/button/ActionButton";
import {faRocket, faVial, faArchive} from "@fortawesome/pro-regular-svg-icons";
import LaunchMarketingEmailDialog from "./dialog/LaunchMarketingEmailDialog";
import SendMarketingEmailTestEmailDialog from "./dialog/SendMarketingEmailTestEmailDialog";
import ArchiveMarketingEmailDialog from "./dialog/ArchiveMarketingEmailDialog";
import type {MarketingEmailViewQuery as MarketingEmailViewQueryType} from "./__generated__/MarketingEmailViewQuery.graphql";
import MarketingEmailViewQuery from "./__generated__/MarketingEmailViewQuery.graphql";

type MarketingEmailViewParams = {
    prepared: any
};

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type MarketingEmailViewProps = MarketingEmailViewParams & ConnectedProps<typeof connector>;

const MarketingEmailView: React.FC<MarketingEmailViewProps> = (props) => {
    const intl = useIntl();
    const [isSaving, setIsSaving] = useState(false);

    const query = usePreloadedQuery<MarketingEmailViewQueryType>(
        graphql__3455b2c26090aa08ea9d,
        props.prepared.marketingEmailQuery
    );

    const [commitRenameMutation] = useMutation(graphql__bb52042670b7bd167f39);

    const handleTitleEdit = useCallback((newTitle: string) => {
        if (!query.marketingEmail) return;
        setIsSaving(true);
        commitRenameMutation({
            variables: {
                input: {
                    id: query.marketingEmail.id,
                    name: newTitle,
                }
            },
            onCompleted: () => setIsSaving(false),
            onError: () => setIsSaving(false),
        });
    }, [query.marketingEmail, commitRenameMutation]);

    if (!query.marketingEmail) {
        return <p>{intl.formatMessage({id: 'marketingEmail.notFound'})}</p>;
    }

    const marketingEmail = query.marketingEmail;
    const isDraft = marketingEmail.status === 'DRAFT';
    const isSent = marketingEmail.status === 'SENT' || marketingEmail.status === 'PARTIALLY_SENT';
    const isArchived = !!marketingEmail.archivedAt;

    // Status badge configuration
    const getStatusBadge = () => {
        const status = marketingEmail.status;
        const statusLabels: Record<string, string> = {
            'DRAFT': intl.formatMessage({id: 'marketingEmail.status.draft'}),
            'SENDING': intl.formatMessage({id: 'marketingEmail.status.sending'}),
            'SENT': intl.formatMessage({id: 'marketingEmail.status.sent'}),
            'PARTIALLY_SENT': intl.formatMessage({id: 'marketingEmail.status.partiallySent'}),
        };
        return {
            value: status,
            label: statusLabels[status] || status,
            states: {
                gray: ['DRAFT'],
                green: ['SENT'],
                yellow: ['SENDING'],
                red: ['PARTIALLY_SENT'],
            },
        };
    };

    const showLaunchDialog = () => {
        props.dispatchShowModal({
            body: <LaunchMarketingEmailDialog queryRef={marketingEmail} handleCloseDialog={props.dispatchHideModal}/>
        });
    };

    const showTestEmailDialog = () => {
        props.dispatchShowModal({
            body: <SendMarketingEmailTestEmailDialog queryRef={marketingEmail} handleCloseDialog={props.dispatchHideModal}/>
        });
    };

    const showArchiveDialog = () => {
        props.dispatchShowModal({
            body: <ArchiveMarketingEmailDialog queryRef={marketingEmail} handleCloseDialog={props.dispatchHideModal}/>
        });
    };

    const breadcrumbNavigationItems = [
        {label: intl.formatMessage({id: 'breadcrumb.marketing'}), to: '/marketing'},
        {label: intl.formatMessage({id: 'breadcrumb.marketingEmails'}), to: '/marketing/emails'},
        {label: marketingEmail.name, to: '/marketing/emails/' + marketingEmail.id},
    ];

    const secondaryNavigationItems = [
        {
            label: intl.formatMessage({id: 'tab.marketingEmail.details'}),
            to: '/marketing/emails/' + marketingEmail.id,
            end: true,
        },
        {
            label: intl.formatMessage({id: 'tab.marketingEmail.delivery'}),
            to: '/marketing/emails/' + marketingEmail.id + '/delivery',
        },
        {
            label: intl.formatMessage({id: 'tab.marketingEmail.unsubscribes'}),
            to: '/marketing/emails/' + marketingEmail.id + '/unsubscribes',
        },
    ];

    // Action buttons for the header
    const headerActions = (
        <>
            {isDraft && !isArchived && (
                <>
                    <ActionButton
                        icon={faVial}
                        label={intl.formatMessage({id: 'button.sendTestEmail'})}
                        onClick={showTestEmailDialog}
                    />
                    <ActionButton
                        icon={faRocket}
                        label={intl.formatMessage({id: 'button.launchMarketingEmail'})}
                        onClick={showLaunchDialog}
                    />
                </>
            )}
            {isSent && !isArchived && (
                <ActionButton
                    icon={faArchive}
                    title={intl.formatMessage({id: 'button.archiveMarketingEmail'})}
                    onClick={showArchiveDialog}
                />
            )}
        </>
    );

    return (
        <>
            <BreadcrumbNavigation items={breadcrumbNavigationItems}/>
            <MarketingHeader
                type="marketingEmail"
                title={marketingEmail.name}
                identifier={marketingEmail.marketingEmailIdentifier}
                badge={getStatusBadge()}
                onTitleEdit={isDraft && !isArchived ? handleTitleEdit : undefined}
                isSaving={isSaving}
                actions={headerActions}
            />
            <SecondaryNavigation items={secondaryNavigationItems}/>
            <div className="mx-auto lg:px-8 grid grid-cols-1 gap-x-4 gap-y-8">
                <Outlet context={{marketingEmail, query}} />
            </div>
        </>
    );
}

export default connector(MarketingEmailView);

function prepare_MarketingEmailView(params, relayEnvironment) {
    return {
        marketingEmailQuery: loadQuery(
            relayEnvironment,
            MarketingEmailViewQuery,
            {marketingEmailId: params.id},
            {fetchPolicy: 'store-and-network'},
        ),
    };
}

export {prepare_MarketingEmailView};
